/**
 * Hook para buscar e gerir estatísticas diárias
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import estatisticasDiariasApi from '../services/estatisticasDiariasApi';

// Períodos disponíveis
export const PERIODOS = {
  ONTEM: 'ontem',
  ULTIMOS_7_DIAS: '7dias',
  MES_CORRENTE: 'mes_corrente',
  ULTIMO_MES: 'ultimo_mes',
  ULTIMOS_3_MESES: '3meses',
  ULTIMOS_6_MESES: '6meses',
  ULTIMOS_12_MESES: '12meses'
};

export const PERIODOS_LABELS = {
  [PERIODOS.ONTEM]: 'Ontem',
  [PERIODOS.ULTIMOS_7_DIAS]: 'Últimos 7 dias',
  [PERIODOS.MES_CORRENTE]: 'Mês corrente',
  [PERIODOS.ULTIMO_MES]: 'Último mês',
  [PERIODOS.ULTIMOS_3_MESES]: 'Últimos 3 meses',
  [PERIODOS.ULTIMOS_6_MESES]: 'Últimos 6 meses',
  [PERIODOS.ULTIMOS_12_MESES]: 'Últimos 12 meses'
};

// Calcular datas para cada período
// Nota: Os dados de "hoje" só são calculados amanhã às 06:00, por isso usamos "ontem" como dataFim
function calcularDatas(periodo) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  
  const ontem = new Date(hoje);
  ontem.setDate(ontem.getDate() - 1);
  
  let dataInicio, dataFim;
  
  switch (periodo) {
    case PERIODOS.ONTEM:
      dataFim = new Date(ontem);
      dataInicio = new Date(ontem);
      break;
      
    case PERIODOS.ULTIMOS_7_DIAS:
      dataFim = new Date(ontem);
      dataInicio = new Date(ontem);
      dataInicio.setDate(dataInicio.getDate() - 6); // ontem - 6 = 7 dias inclusive
      break;
      
    case PERIODOS.MES_CORRENTE:
      dataFim = new Date(ontem);
      dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      break;
      
    case PERIODOS.ULTIMO_MES:
      dataFim = new Date(hoje.getFullYear(), hoje.getMonth(), 0); // último dia do mês anterior
      dataInicio = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      break;
      
    case PERIODOS.ULTIMOS_3_MESES:
      dataFim = new Date(ontem);
      dataInicio = new Date(ontem);
      dataInicio.setMonth(dataInicio.getMonth() - 3);
      break;
      
    case PERIODOS.ULTIMOS_6_MESES:
      dataFim = new Date(ontem);
      dataInicio = new Date(ontem);
      dataInicio.setMonth(dataInicio.getMonth() - 6);
      break;
      
    case PERIODOS.ULTIMOS_12_MESES:
      dataFim = new Date(ontem);
      dataInicio = new Date(ontem);
      dataInicio.setMonth(dataInicio.getMonth() - 12);
      break;
      
    default:
      dataFim = new Date(ontem);
      dataInicio = new Date(ontem);
      dataInicio.setDate(dataInicio.getDate() - 6);
  }
  
  return {
    dataInicio: dataInicio.toISOString().split('T')[0],
    dataFim: dataFim.toISOString().split('T')[0]
  };
}

export function useEstatisticasDiarias() {
  const [periodo, setPeriodo] = useState(PERIODOS.ULTIMOS_7_DIAS);
  const [entidadeSelecionada, setEntidadeSelecionada] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [totais, setTotais] = useState(null);
  const [dadosGrafico, setDadosGrafico] = useState({ agrupamento: 'dia', dados: [] });
  const [entidadesDisponiveis, setEntidadesDisponiveis] = useState([]);

  // Calcular datas baseado no período
  const datas = useMemo(() => calcularDatas(periodo), [periodo]);

  // Buscar entidades disponíveis
  const fetchEntidades = useCallback(async () => {
    try {
      const response = await estatisticasDiariasApi.getEntidades();
      if (response.data?.success) {
        setEntidadesDisponiveis(response.data.data || []);
      }
    } catch (err) {
      console.error('Erro ao buscar entidades:', err);
    }
  }, []);

  // Buscar dados
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = {
      dataInicio: datas.dataInicio,
      dataFim: datas.dataFim,
      entidades: entidadeSelecionada ? [entidadeSelecionada] : undefined
    };

    try {
      const [totaisRes, agrupadasRes] = await Promise.all([
        estatisticasDiariasApi.getTotais(params),
        estatisticasDiariasApi.getAgrupadas(params)
      ]);

      if (totaisRes.data?.success) {
        setTotais(totaisRes.data.data);
      }
      if (agrupadasRes.data?.success) {
        setDadosGrafico(agrupadasRes.data.data);
      }

    } catch (err) {
      console.error('Erro ao buscar estatísticas:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [datas, entidadeSelecionada]);

  // Buscar dados quando filtros mudam
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Buscar entidades no mount
  useEffect(() => {
    fetchEntidades();
  }, [fetchEntidades]);

  // Mudar período
  const mudarPeriodo = useCallback((novoPeriodo) => {
    setPeriodo(novoPeriodo);
  }, []);

  // Mudar entidade
  const mudarEntidade = useCallback((novaEntidade) => {
    setEntidadeSelecionada(novaEntidade);
  }, []);

  // Refresh manual
  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    // Estados
    loading,
    error,
    periodo,
    entidadeSelecionada,
    datas,
    
    // Dados
    totais,
    dadosGrafico,
    entidadesDisponiveis,
    
    // Ações
    mudarPeriodo,
    mudarEntidade,
    refresh
  };
}

export default useEstatisticasDiarias;
